<script lang="ts">
  import { onMount } from "svelte";
  import type { VideoVimeoStoryblok } from "$types/components";
  import type { Player as VimeoPlayer, Options } from "@types/vimeo__player";
  import Player from "@vimeo/player";
  import { IconType } from "$types/atoms.ts";
  import Button from "$lib/atoms/Button.svelte";
  import { ButtonColor } from "$types/atoms.ts";
  import { videoIsPlaying } from "$stores";

  export let blok: VideoVimeoStoryblok;

  let active: boolean = false;
  let seekBar: HTMLInputElement;
  let player: VimeoPlayer;
  let iframeContainer: HTMLElement;
  let wrapper;

  // $: if ($videoIsPlaying && $videoIsPlaying !== blok._uid) {
  // 	pauseVideo();
  // }

  onMount(() => {
    if (!blok.video.vimeo_oembed) return;

    const options: Options = {
      id: blok.video.vimeo_oembed.response.video_id,
      controls: false,
      dnt: true,
    };
    player = new Player(iframeContainer, options);
    // console.log(player);

    player.getVolume().then((volume: number) => {
      if (volume < 1) {
        player.setVolume(1);
      }
    });

    player.on("timeupdate", timeupdate);
    player.on("ended", ended);
    player.on("fullscreenchange", fullscreenChange);

    return () => {
      player.off("timeupdate", timeupdate);
      player.off("ended", ended);
      player.off("fullscreenchange", fullscreenChange);
    };
  });

  function timeupdate(data: { percent: number }) {
    const percentage = (data.percent * 100).toFixed(2);
    wrapper.style.setProperty("--input-range", `${percentage}%`);

    seekBar.value = percentage;
  }

  function ended() {
    active = false;
    wrapper.style.setProperty("--input-range", `${0}%`);
    player.loadVideo(blok.video.vimeo_oembed.response.video_id);
  }

  async function seek(e: Event) {
    e.stopPropagation();
    const target = e.target as HTMLInputElement;
    const percentage: string = target.value;
    wrapper.style.setProperty("--input-range", `${percentage}%`);

    const newTime = (percentage / 100) * (await player.getDuration());
    player.setCurrentTime(newTime);
  }

  function togglePlayPause(e: MouseEvent) {
    e.preventDefault();
    if (!active) {
      playVideo();
    } else {
      pauseVideo();
    }
  }

  videoIsPlaying.subscribe((val) => (val !== blok._uid ? pauseVideo() : null));

  function fullscreenChange(event) {
    if (!event.fullscreen) {
      pauseVideo();
    }
  }

  function playVideo() {
    $videoIsPlaying = blok._uid;

    player.play();
    if (navigator.userAgent.match(/(iPhone|iPod|iPad)/i)) {
      player.requestFullscreen();
    }
    active = true;
  }

  function pauseVideo() {
    player ? player.pause() : null;

    active = false;
    $videoIsPlaying = "";
  }

  function formatDuration(duration: number) {
    let minutes: number = Math.floor(duration / 60);
    let seconds: number = duration - minutes * 60;
    return minutes + ":" + (seconds < 10 ? "0" : "") + seconds;
  }
</script>

{#if blok.video}
  {#if blok.video.vimeo_oembed}
    <div
      class="video-vimeo"
      bind:this={wrapper}
      role="button"
      tabindex="0"
      on:click={(e) => togglePlayPause(e)}
      on:keyup={() => {}}
    >
      <div
        id="iframe-container"
        data-vimeo-url={blok.video.vimeo_raw}
        data-vimeo-controls={false}
        bind:this={iframeContainer}
      />
      <div class="controls">
        <div class="controls-wrapper">
          <Button
            color={ButtonColor.WHITE}
            icon={!active ? IconType.PLAY : IconType.PAUSE}
            reduced
            label="Play video"
          />
          <div class="video-info">
            <p class="video-info-time type:section-title">
              {formatDuration(blok.video.vimeo_oembed.response.duration)}
            </p>
            <p class="video-info-title type:section-title">
              {blok.video.vimeo_oembed.response.title}
            </p>
          </div>
        </div>
        {#if active}
          <input
            id="seek"
            bind:this={seekBar}
            max="100"
            min="0"
            on:click={(e) => seek(e)}
            step="1"
            type="range"
            value="0"
          />
        {/if}
      </div>
    </div>
  {/if}
{/if}

<style lang="scss">
  .video-vimeo {
    width: 100%;
    overflow: hidden;
    position: relative;
    aspect-ratio: 16/9;
    border-radius: var(--border-radius-wrapper);
    -webkit-border-radius: var(--border-radius-wrapper);
    --input-range: 0;
    overflow: hidden;
    position: relative;
    clip-path: inset(0% 0% 0% 0% round 0px);
    cursor: pointer;

    &:before {
      content: "";
      position: absolute;
      left: 0;
      bottom: 0;
      height: 100%;
      width: 100%;
      z-index: 9;
      border-radius: var(--border-radius-wrapper);
      -webkit-border-radius: var(--border-radius-wrapper);
      background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.4) 7.73%,
        rgba(0, 0, 0, 0) 64.88%
      );
      @include breakpoint("small") {
        background: linear-gradient(
          196.22deg,
          rgba(0, 0, 0, 0) 11.27%,
          rgba(0, 0, 0, 0.3) 89.74%
        );
      }
    }
  }

  #iframe-container {
    border-radius: var(--border-radius-wrapper);
    -webkit-border-radius: var(--border-radius-wrapper);
  }

  :global(#iframe-container iframe) {
    box-sizing: border-box;
    width: 177.77777778vh;
    height: 100%;
    min-width: 100%;
    min-height: 100%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .controls {
    display: flex;
    position: absolute;
    z-index: 99;
    bottom: 0;
    left: 24px;
    width: calc(100% - 48px);

    @include breakpoint("small") {
      left: 32px;
      width: calc(100% - 64px);
    }
  }

  .controls-wrapper {
    display: flex;
    margin-bottom: 32px;

    @include breakpoint("small") {
      margin-bottom: 40px;
    }
  }

  .video-info {
    color: rgb(var(--color-white));
    display: flex;
    flex-direction: column;
    margin-left: 24px;

    @include breakpoint("small") {
      margin-left: 32px;
    }

    .video-info-time {
      margin-bottom: 8px;
    }

    .video-info-time,
    .video-info-title {
      color: rgb(var(--color-white));
    }
  }

  #seek {
    position: absolute;
    bottom: 24px;
    width: 100%;
    height: 3px;

    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    cursor: pointer;
    background: rgba(255, 255, 255, 0.12);

    &::-moz-range-progress {
      background-color: white;
    }

    &::before,
    &::after {
      content: "";
      display: block;
      height: 100%;
      position: absolute;
      top: 0;
    }

    &::before {
      left: 0;
      background-color: white;
      width: var(--input-range);
      transition: width 250ms ease-in-out;
    }

    &::after {
      right: 0;
      width: calc(100% - var(--input-range));
      transition: width 250ms ease-in-out;
    }

    &::-webkit-slider-thumb {
      opacity: 0;
    }

    &::-moz-range-thumb {
      opacity: 0;
    }
  }
</style>
